import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import Decimal from 'decimal.js';
import { toHashColorHexString } from '../../utils/colorUtils';
import { Currency } from '../../Currency/Currency';
import { ROI } from '../fundListOptions';
import FundListItemIcons from '../FundListItemIcons';
import { getChooseButtonAriaLabel, getPeriodText } from '../tools/FundList.tools';
import { Card } from '../../Card/Card';
import { Button } from '../../Button/Button';
import { Typography } from '../../Typography/Typography';
import { RiskProgressBar } from '../../RiskProgressBar/RiskProgressBar';
import { useIsMobile } from '../../utils/useIsMobile';
import { useSelect } from '../../Select/Select';
import { LabelWithBar } from '../../LabelWithBar/LabelWithBar';
import { LabelWithTooltip } from '../../LabelWithTooltip/LabelWithTooltip';
import { Divider } from '../../Divider/Divider';
import { FundListItemWithPotentialProfitProps } from './FundListItemWithPotentialProfit.types';
import MultipleWordsTextWithArrow from '../../MultipleWordsTextWithArrow/MultipleWordsTextWithArrow';
import { Title } from '../../Title/Title';

import useStyles from './FundListItemWithPotentialProfit.style';

const FundListItemWithPotentialProfit: React.FC<FundListItemWithPotentialProfitProps> = ({
  fund,
  fundValue,
  roi,
  unitCategoryCode,
  onSelectFund,
  hasMobilePricingDate,
  className,
  isFundInadequate,
  isSelected = false,
  isButtonDisabled = false,
  dynamicUnitCategoryCodeInDetails = false,
  fundDetailsDialogProcessLayout = false,
  renderFundChart,
  renderFundDetailsDialog,
  renderPotentialProfitContent,
  index,
}) => {
  const c = useStyles({});
  const [t] = useTranslation('components');
  const isMobile = useIsMobile();
  const quotesForFund = fund.quotesForUnitCode[unitCategoryCode];
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [mobileRateKey, handleSelectMobileRateKey] = useSelect(roi);

  const renderChartBox = () =>
    renderFundChart(fund, unitCategoryCode, isMobile ? (mobileRateKey as ROI) : roi, {
      isMobile,
      handleSelectMobileRateKey,
    });

  const rateKey = isMobile ? mobileRateKey : roi;
  const periodText = getPeriodText(rateKey, t);

  const fundName = fund.name;
  const chooseButtonAriaLabel = getChooseButtonAriaLabel(fundName, t, isSelected);
  const titleNode = (
    <MultipleWordsTextWithArrow
      text={fundName}
      classNameWrapper={c.textWrapper}
      classNameText={c.text}
      classNameIcon={c.icon}
    />
  );

  const quoteDisplayDate =
    quotesForFund?.date != null && DateTime.fromISO(quotesForFund.date).isValid
      ? DateTime.fromISO(quotesForFund.date).toFormat('dd.MM')
      : undefined;
  const pricingLabelText = quoteDisplayDate
    ? `${t('fund-list.pricing')} ${quoteDisplayDate}`
    : t('fund-list.pricing');

  const potentialProfitSection = (fund.shortText1 || renderPotentialProfitContent) && (
    <div className={c.potentialProfitWrapper}>
      {fund.shortText1 && (
        <div className={c.potentialProfitDesc}>
          <Typography variant="body2" textColor="greys.60">
            {fund.shortText1}
          </Typography>
        </div>
      )}
      {renderPotentialProfitContent && (
        <div className={c.potentialProfitBenefits}>
          {renderPotentialProfitContent()}
        </div>
      )}
    </div>
  );

  return (
    <Card size="equal" className={clsx(c.root, className)} hoverable>
      <div className={c.titleBox}>
        <div>
          <FundListItemIcons
            fund={fund}
            fundValue={fundValue}
            isFundInadequate={isFundInadequate}
            umbrellaColor={toHashColorHexString(fund?.umbrella?.colorHex)}
          />
          {renderFundDetailsDialog?.(fund, unitCategoryCode, index, {
            isMobile,
            isSelected,
            isButtonDisabled,
            isFundInadequate,
            roi: isMobile ? (mobileRateKey as ROI) : roi,
            fundDetailsDialogProcessLayout,
            dynamicUnitCategoryCodeInDetails,
            onSelectFund: onSelectFund ? () => onSelectFund(fund, setIsButtonLoading) : undefined,
            mobileRateKey: mobileRateKey as ROI,
            className: c.button,
            customButton: <Title
                            title={titleNode}
                            titleComponent="h4"
                            titleVariant="h4"
                            titleFontWeight="bold"
                          />,
            titleAsButton: true,
          })}
          {fund.previousName && (
            <Typography variant="body2" textColor="greys.60" className={c.oldFundName}>
              {fund.previousName}
            </Typography>
          )}
          {isMobile && potentialProfitSection}
        </div>
        {!!onSelectFund && (
          <div className={c.actionBox}>
            <div className={c.buttonBox}>
              <Button
                disabled={isButtonDisabled}
                isLoading={isButtonLoading}
                buttonType="secondary"
                onClick={() => onSelectFund(fund, setIsButtonLoading)}
                aria-label={chooseButtonAriaLabel}
                buttonVariant="small"
              >
                {t('fund-list.choose')}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className={c.infoWrapper}>
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={t('fund-list.fundType')}
            preTitle={fund.fundType.tooltipTitle}
            title={fund.fundType.description}
            size="s"
            color="graphite"
          />
          <LabelWithBar size="big" text={fund.fundType.name} color={toHashColorHexString(fund?.umbrella?.colorHex)} />
        </div>
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={`${t('fund-list.risk')}: ${fund.risk.filterLabel}`}
            title={t('fund-details-dialog.risk-tooltip')}
            size="s"
            color="graphite"
          />
          <RiskProgressBar riskValue={fund.risk.value} withRiskValueLabel />
        </div>
        {isMobile && <Divider fullWidth={true} size="small" />}
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={t('fund-list.horizon')}
            title={t('fund-details-dialog.horizon-tooltip')}
            size="s"
            color="graphite"
          />
          <Typography component="span">{t(`fund-list.options.horizon.${fund.horizonValue}-years`)}</Typography>
        </div>
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={pricingLabelText}
            title={t('fund-details-dialog.pricing-tooltip')}
            size="s"
            color="graphite"
          />
          {quotesForFund ? (
            <Currency
              valueFontWeight="bold"
              value={new Decimal(quotesForFund.value).toNumber()} />
          ) : (
            <Typography component="span">{t('fund-list.no-pricing')}</Typography>
          )}
        </div>
        {isMobile && <Divider fullWidth={true} size="small" />}
        <div className={c.infoBox}>
          <LabelWithTooltip
            labelText={`${t('fund-list.roi')} ${rateKey}`}
            title={t('fund-list.roi-tooltip', { period: periodText })}
            size="s"
            color="graphite"
          />
          {renderChartBox()}
        </div>
      </div>

      {!isMobile && potentialProfitSection}
    </Card>
  );
};

export { FundListItemWithPotentialProfit };
